import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import SoftBox from "components/SoftBox";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import Header from "layouts/applications/calendar/components/Header";
import CensusApi from "../../../api/census";

dayjs.extend(isoWeek);

function Calendar() {
  const [censusData, setCensusData] = useState({ weekDays: [], patientRows: [] });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const getThreeWeekRange = (page) => {
    const startOfCurrentWeek = dayjs().isoWeekday(1); 
    const start = startOfCurrentWeek.subtract(page * 3, "week"); 
    const end = start.add(2, "week").isoWeekday(7); 
    return { start, end };
  };

  const generateDaysArray = (start, end) => {
    const days = [];
    let current = start.clone();
    while (current.isBefore(end) || current.isSame(end, "day")) {
      days.push({
        key: current.format("YYYY-MM-DD"),
        label: current.format("ddd DD/MM"),
      });
      current = current.add(1, "day");
    }
    return days;
  };

  const fetchData = () => {
    setLoading(true);
    const { start, end } = getThreeWeekRange(page);

    CensusApi.getWeeklyCensusData(0) 
      .then((response) => {
        if (response && response.data) {
          const daysArray = generateDaysArray(start, end);
          setCensusData({
            weekDays: daysArray,
            patientRows: response.data.patientRows,
          });
        } else {
          setError("Failed to fetch census data: Invalid response format.");
        }
      })
      .catch((error) => {
        console.error("API call failed:", error);
        setError("Failed to fetch census data. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox pt={3} sx={{ textAlign: "center" }}>
          <Typography variant="h5">Loading...</Typography>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox pt={3} sx={{ textAlign: "center" }}>
          <Typography variant="h5" color="error">{error}</Typography>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const { start, end } = getThreeWeekRange(page);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pt={3}>
        <SoftBox display="flex" justifyContent="space-between" mt={1} mb={4} mx={2}>
          <Header />
          <Box>
            <Button
              variant="outlined"
              onClick={() => setPage((prev) => prev + 1)}
              sx={{ mr: 1 }}
            >
              ⬅️ Prev
            </Button>
            <Button
              variant="outlined"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
              disabled={page === 0}
            >
              Next ➡️
            </Button>
          </Box>
        </SoftBox>

        <Typography variant="subtitle1" sx={{ mb: 2, ml: 2 }}>
          Showing from <b>{start.format("DD/MM/YYYY")}</b> to <b>{end.format("DD/MM/YYYY")}</b>
        </Typography>

        <Box
          sx={{
            display: "flex",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: "0 0 auto",
              width: "400px",
              position: "sticky",
              left: 0,
              zIndex: 2,
              background: "#fff",
              borderRight: "1px solid #ddd",
            }}
          >
            <Grid container className="table-header" sx={{ background: "#f8f9fa" }}>
              <Grid item xs={4} sx={{ padding: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">mr Number</Typography>
              </Grid>
              <Grid item xs={4} sx={{ padding: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">Patient Name</Typography>
              </Grid>
              <Grid item xs={4} sx={{ padding: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">Insurance</Typography>
              </Grid>
            </Grid>

            {censusData.patientRows.map((patient, rowIndex) => (
              <Grid
                container
                key={patient.mrNumber}
                sx={{
                  background: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
                  borderBottom: "1px solid #eee",
                }}
              >
                <Grid item xs={4} sx={{ padding: 1 }}>{patient.mrNumber}</Grid>
                <Grid item xs={4} sx={{ padding: 1, overflowX: "auto" }}>{patient.patientName}</Grid>
                <Grid item xs={4} sx={{ padding: 1, overflowX: "auto" }}>{patient.insurance}</Grid>
              </Grid>
            ))}
          </Box>

          <Box sx={{ flex: 1, overflowX: "auto" }}>
            <Box sx={{ minWidth: `${censusData.weekDays.length * 120}px` }}>
              {/* Encabezado */}
              <Grid container className="table-header" sx={{ background: "#f8f9fa" }}>
                {censusData.weekDays.map((day) => (
                  <Grid item key={day.key} sx={{ width: "120px", padding: 1, textAlign: "center" }}>
                    <Typography variant="subtitle2" fontWeight="bold">{day.label}</Typography>
                  </Grid>
                ))}
              </Grid>

              {censusData.patientRows.map((patient, rowIndex) => (
                <Grid
                  container
                  key={patient.mrNumber}
                  sx={{
                    background: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {censusData.weekDays.map((day) => {
                    const dailyData = patient.days?.[day.key];
                    const cellBg = dailyData && dailyData.billed ? "#d4edda" : "#f8d7da";
                    return (
                      <Grid
                        item
                        key={day.key}
                        sx={{
                          width: "120px",
                          padding: 1,
                          textAlign: "center",
                          background: dailyData ? cellBg : "none",
                        }}
                      >
                        {dailyData ? dailyData.levelOfCare : "-"}
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </Box>
          </Box>
        </Box>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Calendar;
